/**
 * Custom Email Signup Form Component
 *
 * Handles form submission and validation for custom email signup form.
 */
class CustomEmailSignupForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector(".custom-email-signup-form");
    this.emailInput = this.querySelector(".custom-email-signup-form__input");
    this.termsCheckbox = this.querySelector(
      ".custom-email-signup-form__checkbox"
    );
    this.errorMessage = this.querySelector(
      ".custom-email-signup-form__message--error"
    );
    this.successMessage = this.querySelector(
      ".custom-email-signup-form__message--success"
    );
    this.recaptchaSlot = this.querySelector("[data-recaptcha-slot]");
    /** @type {number | null} */
    this.recaptchaWidgetId = null;

    if (this.form) {
      this.handleSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener("submit", this.handleSubmit);
      // reCAPTCHA (~350 KB) is only loaded once the visitor starts using the form
      this.renderRecaptcha = this.renderRecaptcha.bind(this);
      this.form.addEventListener("focusin", this.renderRecaptcha, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.form && this.handleSubmit) {
      this.form.removeEventListener("submit", this.handleSubmit);
      this.form.removeEventListener("focusin", this.renderRecaptcha);
    }
  }

  renderRecaptcha() {
    const miloRecaptcha = /** @type {any} */ (window).miloRecaptcha;
    if (!this.recaptchaSlot || !miloRecaptcha || this.recaptchaRequested) return;
    this.recaptchaRequested = true;
    miloRecaptcha.render(this.recaptchaSlot).then(
      /** @param {number} id */
      (id) => {
        this.recaptchaWidgetId = id;
      }
    );
  }

  /**
   * Shows error message
   * @param {string} message - The error message to display
   */
  showError(message) {
    this.hideSuccess();
    if (this.errorMessage) {
      const errorElement = /** @type {HTMLElement} */ (this.errorMessage);
      const messageSpan = errorElement.querySelector("span");
      if (messageSpan) {
        messageSpan.textContent = message;
      }
      errorElement.style.display = "flex";
    }
  }

  /**
   * Hides error message
   */
  hideError() {
    if (this.errorMessage) {
      /** @type {HTMLElement} */ (this.errorMessage).style.display = "none";
    }
  }

  /**
   * Shows success message
   */
  showSuccess() {
    this.hideError();
    if (this.successMessage) {
      /** @type {HTMLElement} */ (this.successMessage).style.display = "flex";

      // Clear form
      if (this.form) {
        /** @type {HTMLFormElement} */ (this.form).reset();
      }
    }
  }

  /**
   * Hides success message
   */
  hideSuccess() {
    if (this.successMessage) {
      /** @type {HTMLElement} */ (this.successMessage).style.display = "none";
    }
  }

  /**
   * Submits email to API
   * @param {string} email - The email address to submit
   */
  async submitEmail(email) {
    const miloRecaptcha = /** @type {any} */ (window).miloRecaptcha;
    const recaptchaToken = miloRecaptcha
      ? miloRecaptcha.getResponse(this.recaptchaWidgetId)
      : "";
    if (!recaptchaToken) {
      this.renderRecaptcha();
      this.showError("Please confirm you are not a robot.");
      return;
    }

    try {
      const response = await fetch(
        "https://api-v2.miloo.com/api/v1.5/odoo/newsletter",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            lng: "en",
            recaptchaToken,
          }),
        }
      );

      if (!response.ok) {
        this.showError("Something went wrong. Please try again later.");
      }

      this.showSuccess();
    } catch (error) {
      this.showError("Something went wrong. Please try again later.");
    } finally {
      if (miloRecaptcha) miloRecaptcha.reset(this.recaptchaWidgetId);
    }
  }

  /**
   * Handles form submission
   * @param {SubmitEvent} event - The submit event
   */
  handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.emailInput || !this.termsCheckbox) return;

    // Hide any previous messages
    this.hideError();
    this.hideSuccess();

    const email = /** @type {HTMLInputElement} */ (
      this.emailInput
    ).value.trim();
    const termsAccepted = /** @type {HTMLInputElement} */ (this.termsCheckbox)
      .checked;

    // Check HTML5 validation
    const emailInputElement = /** @type {HTMLInputElement} */ (this.emailInput);
    if (!emailInputElement.checkValidity()) {
      emailInputElement.reportValidity();
      this.showError("Please enter a valid email address");
      return;
    }

    if (!termsAccepted) {
      this.showError("You must agree to the terms and conditions");
      return;
    }

    // Log the submitted values
    console.log("Form submitted with values:");
    console.log("Email:", email);
    console.log("Terms accepted:", termsAccepted);

    // Make API request
    this.submitEmail(email);
  }
}

if (!customElements.get("custom-email-signup-form")) {
  customElements.define("custom-email-signup-form", CustomEmailSignupForm);
}
